import { object } from '@art-suite/art-core-ts';
import { css } from '@emotion/react';
import { useMemo, useState } from 'react';

import { ApiFeatureFlagRecord, ApiFeatureFlagScope, ApiUser, FEATURE_FLAG_NAMES, KaiProApiClient, NAMED_FEATURE_FLAGS } from '@kaiizen/kai-pro-api';
import { useAllFeatureFlagRecords, useAllOrgs, useKaiProApiClient } from '@kaiizen/kai-pro-api-react-state';
import { useCachedQuery } from '@kaiizen/react-persistent-cache';

import { useAuth, useConfirmationModal, useToast } from '@/Client/State';
import {
  SETTINGS_BODY_TEXT_LIGHT_STYLE,
  SETTINGS_BODY_TEXT_STYLE,
  SETTINGS_CAPTION_STYLE,
  SETTINGS_COLORS,
  SETTINGS_PANEL_HEADER_STYLE,
  SETTINGS_PANEL_STYLE,
  SETTINGS_SPACING,
  SETTINGS_SUBHEADING_STYLE,
  SETTINGS_TOGGLE_KNOB_OFF_STYLE,
  SETTINGS_TOGGLE_KNOB_STYLE,
  SETTINGS_TOGGLE_TRACK_OFF_STYLE,
  SETTINGS_TOGGLE_TRACK_STYLE,
} from '@/Client/Styles';
import { pxToRem } from '@/Client/Styles/pxToRem';
import { LabeledSelect } from '@/Client/Widgets';
import { CommonActionButton, CommonToggle } from '@kaiizen/kai-shiki/react';
import {
  ALL_ORGS,
  ALL_USERS,
  buildDeleteConfirmation,
  buildScopeConfirmation,
  countUnchangedOverrides,
  describeFlagsPanelTitle,
  describeFlagWrite,
  describeSelectionTarget,
  getObsoleteFlagRecords,
  getOrgDisplayName,
  getRecordsForFlagSelection,
  getScopeTargetLabel,
  getUserDisplayName,
  getUserSelectorLabel,
  hasUnchangedOverrides,
  OBSOLETE_FLAG_SELECTION,
  resolveSelectionScope,
} from './superAdminFeatureFlagsLib';

const ADMIN_SECTION_STYLE = css`
  label: ADMIN_SECTION_STYLE_FEATURE_FLAGS;
  width: 100%;
  padding: ${SETTINGS_SPACING.sectionInsetTop}px ${SETTINGS_SPACING.gutterX}px 0;
  display: flex;
  flex-direction: column;
  gap: 18px;
`;

const TAB_BAR_STYLE = css`
  label: TAB_BAR_STYLE;
  display: flex;
  gap: 8px;
`;

const TAB_STYLE = {
  minHeight: 32,
  padding: '0 16px',
  border: `1px solid ${SETTINGS_COLORS.border}`,
  borderRadius: 5,
  background: SETTINGS_COLORS.white,
  color: SETTINGS_COLORS.textMuted,
  textTransform: 'uppercase',
  whiteSpace: 'nowrap',
  fontFamily: 'CircularXX, sans-serif',
  fontSize: pxToRem(12),
  fontWeight: 700,
  lineHeight: 1.167,
  letterSpacing: '0.24px',
  cursor: 'pointer',
} as const;

const TAB_SELECTED_STYLE = {
  background: SETTINGS_COLORS.green,
  borderColor: SETTINGS_COLORS.green,
  color: SETTINGS_COLORS.white,
} as const;

const SCOPE_SELECTORS_STYLE = css`
  label: SCOPE_SELECTORS_STYLE;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${SETTINGS_SPACING.gutterX}px;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const FLAG_SELECTOR_ROW_STYLE = css`
  label: FLAG_SELECTOR_ROW_STYLE;
  max-width: 640px;
`;

// Global reach is the one scope worth shouting about, so the heading itself says so.
const PANEL_HEADER_GLOBAL_STYLE = css`
  label: PANEL_HEADER_GLOBAL_STYLE;
  color: ${SETTINGS_COLORS.red};
  font-weight: 700;
`;

const OBSOLETE_NOTE_STYLE = css`
  label: OBSOLETE_NOTE_STYLE;
  ${SETTINGS_BODY_TEXT_STYLE}
`;

const FLAG_ROW_STYLE = css`
  label: FLAG_ROW_STYLE;
  padding: 14px ${SETTINGS_SPACING.gutterX}px;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 24px;
  border-bottom: 1px solid ${SETTINGS_COLORS.border};

  &:last-of-type {
    border-bottom: 0;
  }
`;

const FLAG_TEXT_BLOCK_STYLE = css`
  label: FLAG_TEXT_BLOCK_STYLE;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const FLAG_NAME_STYLE = css`
  label: FLAG_NAME_STYLE;
  ${SETTINGS_SUBHEADING_STYLE}
`;

const FLAG_DESCRIPTION_STYLE = css`
  label: FLAG_DESCRIPTION_STYLE;
  ${SETTINGS_BODY_TEXT_STYLE}
`;

const FLAG_CONTROLS_STYLE = css`
  label: FLAG_CONTROLS_STYLE;
  display: flex;
  align-items: center;
  gap: 14px;
  flex: 0 0 auto;
  padding-top: 2px;
`;

const LIST_SCROLL_STYLE = css`
  label: LIST_SCROLL_STYLE;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
`;

const LIST_INNER_STYLE = css`
  label: LIST_INNER_STYLE;
  min-width: 960px;
`;

const COLUMNS = 'minmax(250px, 1fr) 300px 110px 150px 90px';

const HEADER_ROW_STYLE = css`
  label: HEADER_ROW_STYLE;
  display: grid;
  grid-template-columns: ${COLUMNS};
  gap: ${SETTINGS_SPACING.gutterX}px;
  align-items: center;
  padding: 10px ${SETTINGS_SPACING.gutterX}px;
  background: ${SETTINGS_COLORS.panelHeaderBackground};
  border-bottom: 1px solid ${SETTINGS_COLORS.border};
`;

const HEADER_CELL_STYLE = css`
  label: HEADER_CELL_STYLE;
  ${SETTINGS_CAPTION_STYLE}
  color: ${SETTINGS_COLORS.textFieldLabel};
  text-transform: uppercase;
`;

const RECORD_ROW_STYLE = css`
  label: RECORD_ROW_STYLE;
  display: grid;
  grid-template-columns: ${COLUMNS};
  gap: ${SETTINGS_SPACING.gutterX}px;
  align-items: center;
  padding: 14px ${SETTINGS_SPACING.gutterX}px;
  border-bottom: 1px solid ${SETTINGS_COLORS.border};

  &:last-of-type {
    border-bottom: 0;
  }
`;

const CELL_STYLE = css`
  label: CELL_STYLE;
  ${SETTINGS_BODY_TEXT_STYLE}
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const WRAP_CELL_STYLE = css`
  label: WRAP_CELL_STYLE;
  ${SETTINGS_BODY_TEXT_STYLE}
  overflow-wrap: anywhere;
  word-break: break-word;
  white-space: normal;
`;

const EMPTY_STYLE = css`
  label: EMPTY_STYLE;
  ${SETTINGS_BODY_TEXT_LIGHT_STYLE}
  padding: 14px ${SETTINGS_SPACING.gutterX}px;
`;

const DELETE_BUTTON_STYLE = {
  minHeight: 32,
  padding: '0 12px',
  border: `1px solid ${SETTINGS_COLORS.border}`,
  borderRadius: 5,
  background: SETTINGS_COLORS.white,
  color: SETTINGS_COLORS.red,
  textTransform: 'uppercase',
  whiteSpace: 'nowrap',
  fontFamily: 'CircularXX, sans-serif',
  fontSize: pxToRem(12),
  fontWeight: 700,
  lineHeight: 1.167,
  letterSpacing: '0.24px',
  cursor: 'pointer',
} as const;

const useUsersForOrg = (orgId: string) => {
  const kaiProApiClient = useKaiProApiClient();

  const query = useCachedQuery(
    `users/byOrg/${orgId}`,
    async () => {
      const { token } = await kaiProApiClient.switchOrg(orgId);
      const apiClientForOrg = !token ? kaiProApiClient : new KaiProApiClient({ apiUrl: kaiProApiClient.options.apiUrl, token });
      return apiClientForOrg.getUsers();
    },
    { enabled: !!orgId && !!kaiProApiClient.token },
  );

  return { ...query, usersForOrg: query.data ?? [] };
};

/**
 * Resolves users by id so a flag record always names a person. The org-scoped
 * list only covers the selected org, so on its own it leaves every user-scoped
 * record from any other org showing nothing but a truncated id.
 */
const useUsersByIds = (userIds: string[]) => {
  const kaiProApiClient = useKaiProApiClient();
  const distinctUserIds = useMemo(() => [...new Set(userIds)].sort(), [userIds]);

  const query = useCachedQuery(
    `users/byIds/${distinctUserIds.join(',')}`,
    async () => {
      // One unreachable user must not blank out the names of all the others.
      const users = await Promise.all(
        distinctUserIds.map(userId => kaiProApiClient.getUser(userId).catch(() => undefined)),
      );
      return users.filter((user): user is ApiUser => !!user);
    },
    { enabled: distinctUserIds.length > 0 && !!kaiProApiClient.token },
  );

  return query.data ?? [];
};

const resolveEffectiveValue = (flags: ApiFeatureFlagRecord[], flagName: string, orgId: string, userId: string): boolean => {
  const globalFlag = flags.find(f => f.flagName === flagName && f.scope === 'global');
  let value = globalFlag?.enabled;
  if (orgId !== ALL_ORGS) {
    const orgFlag = flags.find(f => f.flagName === flagName && f.scope === 'org' && f.orgId === orgId);
    if (orgFlag) value = orgFlag.enabled;
  }
  if (userId !== ALL_USERS) {
    const userFlag = flags.find(f => f.flagName === flagName && f.scope === 'user' && f.userId === userId);
    if (userFlag) value = userFlag.enabled;
  }
  return value ?? false;
};

const ALPHABETICAL_FEATURE_FLAG_NAMES = [...FEATURE_FLAG_NAMES].sort((a, b) => a.localeCompare(b));

const useAllEffectiveFeatureFlags = (flags: ApiFeatureFlagRecord[], orgId: string, userId: string) =>
  useMemo(
    () => Object.fromEntries(FEATURE_FLAG_NAMES.map(name => [name, resolveEffectiveValue(flags, name, orgId, userId)])) as Record<string, boolean>,
    [flags, orgId, userId],
  );

const OrgSelector = ({ selectedOrgId, setSelectedOrgId }: { selectedOrgId: string; setSelectedOrgId: (orgId: string) => void }) => {
  const { allOrgs } = useAllOrgs();

  const orgItems = useMemo(
    () => [
      { value: ALL_ORGS, label: 'All orgs' },
      ...allOrgs
        .map(org => ({ value: org.id, label: org.name }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    ],
    [allOrgs],
  );

  return (
    <LabeledSelect
      label={'Organizations'}
      value={selectedOrgId}
      items={orgItems}
      onUpdate={setSelectedOrgId}
    />
  );
};

const UserSelector = (
  { users, selectedUserId, setSelectedUserId }: { users: ApiUser[]; selectedUserId: string; setSelectedUserId: (userId: string) => void },
) => {
  const userItems = useMemo(
    () => [
      { value: ALL_USERS, label: 'All users' },
      ...users
        .map(user => ({ value: user.id, label: getUserSelectorLabel(user) }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    ],
    [users],
  );

  return (
    <LabeledSelect
      label='Users'
      value={selectedUserId}
      items={userItems}
      onUpdate={setSelectedUserId}
    />
  );
};

type Tab = 'all-flags' | 'by-flag';

export const SuperAdminFeatureFlagsManager = () => {
  const { isSuperAdmin, orgId: currentOrgId, userId: currentUserId } = useAuth();
  const { allOrgs } = useAllOrgs();
  const { tryWithErrorToast } = useToast();
  const confirm = useConfirmationModal();

  const [tab, setTab] = useState<Tab>('all-flags');
  const [flagSelection, setFlagSelection] = useState<string | undefined>(undefined);
  // Open on the signed-in user in their current org, so the default gesture
  // changes only your own flag rather than everyone's.
  const [selectedOrgId, setSelectedOrgId] = useState(currentOrgId ?? ALL_ORGS);
  const [selectedUserId, setSelectedUserId] = useState(currentUserId ?? ALL_USERS);
  const { usersForOrg: users } = useUsersForOrg(selectedOrgId);
  const { allFeatureFlagRecords: flagRecords, isLoading: flagRecordsAreLoading, featureFlagRecordMutators } = useAllFeatureFlagRecords();

  const allEffectiveFlags = useAllEffectiveFeatureFlags(flagRecords, selectedOrgId, selectedUserId);

  const flagRecordUserIds = useMemo(
    () => flagRecords.flatMap(flag => (flag.scope === 'user' && flag.userId ? [flag.userId] : [])),
    [flagRecords],
  );
  const usersFromFlagRecords = useUsersByIds(flagRecordUserIds);
  const usersById = useMemo(
    () => object([...usersFromFlagRecords, ...users], { withKey: ({ id }) => id }),
    [usersFromFlagRecords, users],
  );
  const orgsById = useMemo(() => object(allOrgs, { withKey: ({ id }) => id }), [allOrgs]);
  const sortedFlagRecords = useMemo(() => {
    const scopeOrder = { global: 0, org: 1, user: 2 } as const;
    const targetName = (flag: ApiFeatureFlagRecord): string => {
      if (flag.scope === 'org') return flag.orgId ? (orgsById[flag.orgId]?.name ?? flag.orgId) : '';
      if (flag.scope === 'user') return flag.userId ? (usersById[flag.userId]?.name ?? flag.userId) : '';
      return '';
    };
    return [...flagRecords].sort((a, b) => {
      const byName = a.flagName.localeCompare(b.flagName);
      if (byName !== 0) return byName;
      const byScope = scopeOrder[a.scope] - scopeOrder[b.scope];
      if (byScope !== 0) return byScope;
      return targetName(a).localeCompare(targetName(b));
    });
  }, [flagRecords, orgsById, usersById]);
  const obsoleteFlagRecords = useMemo(
    () => getObsoleteFlagRecords(sortedFlagRecords, FEATURE_FLAG_NAMES),
    [sortedFlagRecords],
  );
  const recordsForSelection = useMemo(
    () => getRecordsForFlagSelection(sortedFlagRecords, flagSelection, FEATURE_FLAG_NAMES),
    [sortedFlagRecords, flagSelection],
  );

  // "Obsolete" only exists as a choice while there is something to clean up.
  const flagSelectorItems = useMemo(() => [
    ...FEATURE_FLAG_NAMES.map(name => ({ value: name, label: `${name} (${NAMED_FEATURE_FLAGS[name].description})` })),
    ...(obsoleteFlagRecords.length > 0
      ? [{
        value: OBSOLETE_FLAG_SELECTION,
        label: `Obsolete — ${obsoleteFlagRecords.length} record${obsoleteFlagRecords.length === 1 ? '' : 's'} for flags no longer in the registry`,
      }]
      : []),
  ], [obsoleteFlagRecords]);

  const isShowingObsolete = flagSelection === OBSOLETE_FLAG_SELECTION;
  const orgUserIds = useMemo(() => users.map(({ id }) => id), [users]);
  const scope = resolveSelectionScope(selectedOrgId, selectedUserId);

  if (!isSuperAdmin) return null;

  const selectOrgName = selectedOrgId === ALL_ORGS ? undefined : getOrgDisplayName(orgsById[selectedOrgId]) ?? selectedOrgId;
  const selectedUserDisplayName = selectedUserId === ALL_USERS ? undefined : getUserDisplayName(usersById[selectedUserId]) ?? selectedUserId;

  const flagsPanelTitle = describeFlagsPanelTitle(
    scope,
    selectOrgName,
    selectedUserDisplayName,
    !!currentUserId && selectedUserId === currentUserId,
  );

  const selectionTargetDescription = describeSelectionTarget(scope, selectOrgName, selectedUserDisplayName);

  /**
   * Writes a flag, but first demands an explicit confirmation for any scope
   * wider than a single user, reporting how many narrower overrides the write
   * will leave standing.
   *
   * `writeOrgId` is the org the write targets. Membership can only be counted for
   * the org whose user list is loaded, so an org record for some other org reports
   * an unknown count rather than a wrong one.
   */
  const saveFlagWithScopeGuard = async (
    targetFlagName: string,
    enabled: boolean,
    writeScope: ApiFeatureFlagScope,
    targetDescription: string,
    save: () => Promise<unknown>,
    writeOrgId?: string,
  ) => {
    const runSave = () =>
      tryWithErrorToast(
        describeFlagWrite(targetFlagName, enabled, targetDescription),
        save,
      );

    const knownOrgUserIds = writeOrgId && writeOrgId === selectedOrgId ? orgUserIds : undefined;
    const unchangedOverrides = countUnchangedOverrides(flagRecords, targetFlagName, writeScope, knownOrgUserIds);
    const confirmation = buildScopeConfirmation(targetFlagName, enabled, writeScope, targetDescription, unchangedOverrides);
    if (!confirmation) return runSave();

    // Only worth offering when it would actually show something new.
    const alreadyViewingThisFlag = tab === 'by-flag' && flagSelection === targetFlagName;
    const canInspect = hasUnchangedOverrides(unchangedOverrides) && !alreadyViewingThisFlag;

    await confirm({
      ...confirmation,
      ...(canInspect
        ? {
          alternateLabel: 'View all settings for this flag',
          onAlternate: () => {
            setFlagSelection(targetFlagName);
            setTab('by-flag');
          },
        }
        : {}),
      onConfirm: runSave,
    });
  };

  /**
   * Deletes a record, confirming first unless the record is obsolete — those are
   * pure cleanup and cannot change what any user sees.
   */
  const deleteFlagRecordWithGuard = async (
    flag: ApiFeatureFlagRecord,
    scopeTargetLabel: string,
  ) => {
    const runDelete = () =>
      tryWithErrorToast(
        `delete feature flag ${flag.flagName} for ${scopeTargetLabel}`,
        () => featureFlagRecordMutators.deleteFeatureFlagRecord(flag.id),
      );

    const confirmation = buildDeleteConfirmation(flag, scopeTargetLabel, FEATURE_FLAG_NAMES);
    if (!confirmation) return runDelete();

    await confirm({ ...confirmation, onConfirm: runDelete });
  };

  return (
    <div css={ADMIN_SECTION_STYLE}>
      <div css={TAB_BAR_STYLE}>
        <CommonActionButton
          style={{ ...TAB_STYLE, ...(tab === 'all-flags' ? TAB_SELECTED_STYLE : {}) }}
          aria-pressed={tab === 'all-flags'}
          onClick={() => setTab('all-flags')}
        >
          All Feature Flags
        </CommonActionButton>
        <CommonActionButton
          style={{ ...TAB_STYLE, ...(tab === 'by-flag' ? TAB_SELECTED_STYLE : {}) }}
          aria-pressed={tab === 'by-flag'}
          onClick={() => setTab('by-flag')}
        >
          By Flag
        </CommonActionButton>
      </div>

      {tab === 'all-flags' && (
        <>
          <div css={SCOPE_SELECTORS_STYLE}>
            <OrgSelector
              selectedOrgId={selectedOrgId}
              setSelectedOrgId={orgId => {
                setSelectedOrgId(orgId);
                setSelectedUserId(ALL_USERS);
              }}
            />
            <UserSelector users={users} selectedUserId={selectedUserId} setSelectedUserId={setSelectedUserId} />
          </div>

          <div css={SETTINGS_PANEL_STYLE}>
            <div css={[SETTINGS_PANEL_HEADER_STYLE, scope === 'global' && PANEL_HEADER_GLOBAL_STYLE]}>
              {flagsPanelTitle}
            </div>
            {ALPHABETICAL_FEATURE_FLAG_NAMES.map(name => (
              <div key={name} css={FLAG_ROW_STYLE}>
                <div css={FLAG_TEXT_BLOCK_STYLE}>
                  <div css={FLAG_NAME_STYLE}>{name}</div>
                  <div css={FLAG_DESCRIPTION_STYLE}>{NAMED_FEATURE_FLAGS[name].description}</div>
                </div>
                <div css={FLAG_CONTROLS_STYLE}>
                  <CommonToggle
                    checked={allEffectiveFlags[name] ?? false}
                    trackCss={[SETTINGS_TOGGLE_TRACK_STYLE, !allEffectiveFlags[name] && SETTINGS_TOGGLE_TRACK_OFF_STYLE]}
                    knobCss={[SETTINGS_TOGGLE_KNOB_STYLE, !allEffectiveFlags[name] && SETTINGS_TOGGLE_KNOB_OFF_STYLE]}
                    aria-label={`${allEffectiveFlags[name] ? 'Disable' : 'Enable'} ${name}`}
                    onClick={() =>
                      saveFlagWithScopeGuard(
                        name,
                        !allEffectiveFlags[name],
                        scope,
                        selectionTargetDescription,
                        () => featureFlagRecordMutators.saveFlagForCurrentContext(name, !allEffectiveFlags[name], selectedOrgId, selectedUserId),
                        selectedOrgId,
                      )}
                  />
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {tab === 'by-flag' && (
        <>
          <div css={FLAG_SELECTOR_ROW_STYLE}>
            <LabeledSelect
              label='Feature Flag'
              value={flagSelection ?? ''}
              items={flagSelectorItems}
              onChange={setFlagSelection}
              onUpdate={setFlagSelection}
              placeholder='Select flag...'
            />
          </div>

          {isShowingObsolete && (
            <div css={OBSOLETE_NOTE_STYLE}>
              These records reference flags that are no longer in the registry, so nothing reads them. Deleting them is the only thing left to do.
            </div>
          )}

          <div css={SETTINGS_PANEL_STYLE}>
            <div css={SETTINGS_PANEL_HEADER_STYLE}>
              {isShowingObsolete ? 'Obsolete Records' : flagSelection || 'Records'}
            </div>

            {!flagSelection && <div css={EMPTY_STYLE}>Select a feature flag to see the records set for it.</div>}

            {flagSelection && flagRecordsAreLoading && <div css={EMPTY_STYLE}>Loading feature flags...</div>}

            {flagSelection && !flagRecordsAreLoading && recordsForSelection.length === 0 && (
              <div css={EMPTY_STYLE}>
                {isShowingObsolete
                  ? 'No obsolete feature flag records — nothing to clean up.'
                  : 'No records set for this feature flag.'}
              </div>
            )}

            {flagSelection && !flagRecordsAreLoading && recordsForSelection.length > 0 && (
              <div css={LIST_SCROLL_STYLE}>
                <div css={LIST_INNER_STYLE}>
                  <div css={HEADER_ROW_STYLE}>
                    <div css={HEADER_CELL_STYLE}>Flag Name</div>
                    <div css={HEADER_CELL_STYLE}>Target</div>
                    <div css={HEADER_CELL_STYLE}>Enabled</div>
                    <div css={HEADER_CELL_STYLE}>Created</div>
                    <div css={HEADER_CELL_STYLE}>Actions</div>
                  </div>
                  {recordsForSelection.map(flag => {
                    const scopeTargetLabel = getScopeTargetLabel(flag, orgsById, usersById);
                    const flagDescription = (NAMED_FEATURE_FLAGS as Record<string, { description?: string }>)[flag.flagName]?.description;
                    return (
                      <div key={flag.id} css={RECORD_ROW_STYLE}>
                        <div css={FLAG_TEXT_BLOCK_STYLE}>
                          <div css={FLAG_NAME_STYLE}>{flag.flagName}</div>
                          {flagDescription && <div css={FLAG_DESCRIPTION_STYLE}>{flagDescription}</div>}
                        </div>
                        <div css={WRAP_CELL_STYLE}>{scopeTargetLabel}</div>
                        <div>
                          <CommonToggle
                            checked={flag.enabled}
                            trackCss={[SETTINGS_TOGGLE_TRACK_STYLE, !flag.enabled && SETTINGS_TOGGLE_TRACK_OFF_STYLE]}
                            knobCss={[SETTINGS_TOGGLE_KNOB_STYLE, !flag.enabled && SETTINGS_TOGGLE_KNOB_OFF_STYLE]}
                            aria-label={`${flag.enabled ? 'Disable' : 'Enable'} ${flag.flagName} for ${scopeTargetLabel}`}
                            onClick={() =>
                              saveFlagWithScopeGuard(
                                flag.flagName,
                                !flag.enabled,
                                flag.scope,
                                scopeTargetLabel,
                                () => featureFlagRecordMutators.toggleFeatureFlagRecordEnabled(flag),
                                flag.orgId,
                              )}
                          />
                        </div>
                        <div css={CELL_STYLE}>{new Date(flag.createdAt).toLocaleString()}</div>
                        <div>
                          <CommonActionButton
                            style={DELETE_BUTTON_STYLE}
                            onClick={() => deleteFlagRecordWithGuard(flag, scopeTargetLabel)}
                          >
                            Delete
                          </CommonActionButton>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};
